<template>
  <div class="subpartContainer">
    <div class="title">Last menstrual period</div>
    <el-input
      v-model="this.lastMenstrualPeriod"
      placeholder="MM/DD/YYYY"
      class="inputField"
      @blur="handleMenstrualDateChange"
      :disabled="this.qnrIsCompleted"
    />
  </div>
  <div class="subpartContainer col-container">
    <MenstrualPhaseRadio
      content="Which of the following best describes your current reproductive health status?"
      width="auto"
      mobileWidth="334px"
      :isRequired="true"
      requiredMessage="<br>* Required for populating reference ranges and appropriate report comments."
      :options="reproductiveHealthStageOptions"
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange('basic_info.reproductive_health_stage', $event)
      "
      @removeWarning="removeWarning"
    />
  </div>

  <div class="subpartContainer" v-show="showWarning">
    <SimplifiedNotification
      build_in_theme="red"
      :with_close_btn="false"
    >
    <template #content>
      <span class="text-red-500 " :class="{ 'text-[12px]' : isMobile }">
        This is a mandatory question.
      </span>
    </template>
    </SimplifiedNotification>
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Do you have fairly regular periods?"
      width="120px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="this.hormoneZoomerQnr.basic_info.regular_periods"
      :disabled="this.qnrIsCompleted"
      @onChange="handleValueChange('basic_info.regular_periods', $event)"
    />
  </div>

  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Have you had a hysterectomy (surgical removal of the uterus)?"
      width="120px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="this.hormoneZoomerQnr.basic_info.hysterectomy"
      :disabled="this.qnrIsCompleted"
      @onChange="handleValueChange('basic_info.hysterectomy', $event)"
    />
  </div>
  <div class="subTitleContainer">
    <div class="subTitle">Bone Health and Toxin Exposure</div>
  </div>
  <div
    class="subpartContainer flex flex-row justify-between items-center mb-[12px]"
  >
    <QuestionnaireRadio
      content="Have you ever had a bone density scan?"
      width="120px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="
        this.hormoneZoomerQnr.bone_health_and_toxic_exposure.bone_density_scan
      "
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange(
          'bone_health_and_toxic_exposure.bone_density_scan',
          $event
        )
      "
    />
  </div>
  <div
    class="expandedContainer"
    v-if="
      this.hormoneZoomerQnr.bone_health_and_toxic_exposure.bone_density_scan
    "
  >
    <QuestionnaireRadio
      content="If yes, what was the result?"
      width="165px"
      mobileWidth="151px"
      :options="boneDensityScanOptions"
      :value="
        this.hormoneZoomerQnr.bone_health_and_toxic_exposure.bone_density_result
      "
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange(
          'bone_health_and_toxic_exposure.bone_density_result',
          $event
        )
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Have you experienced any fractures?"
      width="120px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="
        this.hormoneZoomerQnr.bone_health_and_toxic_exposure
          .experienced_fractures
      "
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange(
          'bone_health_and_toxic_exposure.experienced_fractures',
          $event
        )
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Have you ever been exposed to toxic substances?"
      width="120px"
      mobileWidth="106px"
      :options="yesNoUnsureOptions"
      :value="
        this.hormoneZoomerQnr.bone_health_and_toxic_exposure
          .toxic_substance_exposure
      "
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange(
          'bone_health_and_toxic_exposure.toxic_substance_exposure',
          $event
        )
      "
    />
  </div>
  <div class="subTitleContainer">
    <div class="subTitle">Symptom Assessment</div>
    <div class="assessmentHint">
      Please rate the severity of the following symptoms on a scale of
      0-3:&nbsp; &nbsp;
      <b
        >0 = None&nbsp; &nbsp; 1 = Mild&nbsp; &nbsp; 2 = Moderate&nbsp; &nbsp; 3
        = Severe</b
      >
    </div>
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Hot flashes/night sweats"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.hot_flashes"
      :disabled="this.qnrIsCompleted"
      @onChange="handleValueChange('symptom_assessment.hot_flashes', $event)"
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Mood swings/irritability"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.mood_swings"
      :disabled="this.qnrIsCompleted"
      @onChange="handleValueChange('symptom_assessment.mood_swings', $event)"
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Fatigue"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.fatigue"
      :disabled="this.qnrIsCompleted"
      @onChange="handleValueChange('symptom_assessment.fatigue', $event)"
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Vaginal dryness"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.vaginal_dryness"
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange('symptom_assessment.vaginal_dryness', $event)
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Sleep disturbances"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.sleep_disturbances"
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange('symptom_assessment.sleep_disturbances', $event)
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Joint pain"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.joint_pain"
      :disabled="this.qnrIsCompleted"
      @onChange="handleValueChange('symptom_assessment.joint_pain', $event)"
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Loss of muscle mass"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.loss_of_muscle_mass"
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange('symptom_assessment.loss_of_muscle_mass', $event)
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Loss of libido"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.loss_of_libido"
      :disabled="this.qnrIsCompleted"
      @onChange="handleValueChange('symptom_assessment.loss_of_libido', $event)"
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Urinary problems"
      width="auto"
      mobileWidth="77.5px"
      :options="severityOptions"
      :value="this.hormoneZoomerQnr.symptom_assessment.urinary_problems"
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange('symptom_assessment.urinary_problems', $event)
      "
    />
  </div>
  <div class="subTitleContainer">
    <div class="subTitle">Medical History</div>
  </div>
  <div class="subpartContainer col-container">
    <QuestionnaireMultiChoice
      content="Do you have a history of:"
      width="fit-content"
      mobileWidth="fit-content"
      :options="femaleDiseaseHistoryOptions"
      :value="this.personalHistoryArray"
      :disabled="this.qnrIsCompleted"
      @onChange="handlePersonHistoryChange"
    />
  </div>
  <div
    class="expandedContainer"
    v-if="this.personalHistoryArray.includes('other')"
  >
    <div class="title mb-[10px]">Please specify</div>
    <el-input
      v-model="
        this.hormoneZoomerQnr.medical_history.personal_history.other_cancer
      "
      class="expandInputField"
      :type="this.dynamic_input_type"
      :disabled="this.qnrIsCompleted"
      @input="
        handleValueChange(
          'medical_history.personal_history.other_cancer',
          $event
        )
      "
    />
  </div>
  <div class="subpartContainer col-container">
    <QuestionnaireMultiChoice
      content="Do you have any of the following comorbidities:"
      width="fit-content"
      mobileWidth="fit-content"
      :options="femaleComorbidityOptions"
      :value="this.comorbiditiesArray"
      :disabled="this.qnrIsCompleted"
      @onChange="handleComorbidityChange"
    />
  </div>
  <div
    class="expandedContainer"
    v-if="this.comorbiditiesArray.includes('other')"
  >
    <div class="title mb-[10px]">Please specify</div>
    <el-input
      v-model="
        this.hormoneZoomerQnr.medical_history.comorbidities.other_comorbidities
      "
      class="expandInputField"
      :type="this.dynamic_input_type"
      :disabled="this.qnrIsCompleted"
      @input="
        handleValueChange(
          'medical_history.comorbidities.other_comorbidities',
          $event
        )
      "
    />
  </div>
  <div class="subpartContainer col-container">
    <QuestionnaireMultiChoice
      content="Do you have a familial history of:"
      width="fit-content"
      mobileWidth="fit-content"
      :options="femaleFamilyHistoryOptions"
      :value="this.familyHistoryArray"
      :disabled="this.qnrIsCompleted"
      @onChange="handleFamilyHistoryChange"
    />
  </div>
  <div
    class="expandedContainer"
    v-if="this.familyHistoryArray.includes('other')"
  >
    <div class="title mb-[10px]">Please specify</div>
    <el-input
      v-model="
        this.hormoneZoomerQnr.medical_history.family_history
          .other_family_conditions
      "
      class="expandInputField"
      :type="this.dynamic_input_type"
      :disabled="this.qnrIsCompleted"
      @input="
        handleValueChange('family_history.other_family_conditions', $event)
      "
    />
  </div>
  <div class="subTitleContainer">
    <div class="subTitle">Treatment Considerations</div>
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Do you have a hormone treatment preference?"
      width="132px"
      :options="HRTOptions"
      :value="
        this.hormoneZoomerQnr.treatment_considerations
          .hormone_treatment_preference
      "
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange(
          'treatment_considerations.hormone_treatment_preference',
          $event
        )
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Are you allergic to peanuts? (Affects certain forms of HRT)"
      width="132px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="this.hormoneZoomerQnr.treatment_considerations.peanut_allergy"
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange('treatment_considerations.peanut_allergy', $event)
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Do you have sensitive skin? (Affects certain forms of HRT)"
      width="132px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="this.hormoneZoomerQnr.treatment_considerations.sensitive_skin"
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange('treatment_considerations.sensitive_skin', $event)
      "
    />
  </div>
  <div class="subpartContainer">
    <QuestionnaireRadio
      content="Are you currently undergoing HRT and/or taking any medications? "
      width="132px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="
        this.hormoneZoomerQnr.treatment_considerations
          .undergoing_hormone_therapy
      "
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange(
          'treatment_considerations.undergoing_hormone_therapy',
          $event
        )
      "
    />
  </div>
    <div class="subpartContainer">
    <QuestionnaireRadio
      content="Are you currently breastfeeding? "
      width="132px"
      mobileWidth="163px"
      :options="yesNoOptions"
      :value="
        this.hormoneZoomerQnr.basic_info.breastfeeding
      "
      :disabled="this.qnrIsCompleted"
      @onChange="
        handleValueChange(
          'basic_info.breastfeeding',
          $event
        )
      "
    />
  </div>
  <div
    class="expandedContainer"
    v-if="
      this.hormoneZoomerQnr.treatment_considerations.undergoing_hormone_therapy
    "
  >
    <div class="title mb-[10px]">Please specify</div>
    <el-input
      v-model="
        this.hormoneZoomerQnr.treatment_considerations.current_medications
      "
      class="expandInputField"
      :type="this.dynamic_input_type"
      :disabled="this.qnrIsCompleted"
      @input="
        handleValueChange(
          'treatment_considerations.current_medications',
          $event
        )
      "
    />
  </div>
</template>

<script>
import QuestionnaireRadio from "./QuestionnaireRadio.vue";
import QuestionnaireMultiChoice from "./QuestionnaireMultiChoice.vue";
import MenstrualPhaseRadio from "./MenstrualPhaseRadio.vue";
import { useHormoneZoomerStore } from "@/stores/HormoneZoomerStore.ts";
import { mapState, mapActions } from "pinia";
import moment from "moment";
import SimplifiedNotification from "@/components/commons/SimplifiedNotification.vue";
import { useWindowSize } from "@vueuse/core";

import {
  reproductiveHealthStageOptions,
  yesNoOptions,
  severityOptions,
  yesNoUnsureOptions,
  HRTOptions,
  femaleDiseaseHistoryOptions,
  femaleComorbidityOptions,
  femaleFamilyHistoryOptions,
  boneDensityScanOptions,
  premenopausalPhaseResultOptions,
} from "@/utils/questionnaireOptions";

export default {
  name: "FemaleRelatedQuestions",
  components: {
    QuestionnaireRadio,
    MenstrualPhaseRadio,
    QuestionnaireMultiChoice,
    SimplifiedNotification,
  },
  props: {
    showWarning: Boolean,
  },
  data() {
    return {
      lastMenstrualPeriod: "",
      reproductiveHealthStageOptions,
      yesNoOptions,
      severityOptions,
      yesNoUnsureOptions,
      HRTOptions,
      femaleDiseaseHistoryOptions,
      femaleComorbidityOptions,
      femaleFamilyHistoryOptions,
      boneDensityScanOptions,
      premenopausalPhaseResultOptions,
    };
  },
  mounted() {
    this.lastMenstrualPeriod = this.lastMenstrualDate;
  },
  setup() {
    const { width } = useWindowSize();
    return {
      width,
    };
  },
  watch: {
    showWarning: {
      immediate: true,
      handler(newVal) {
        console.log("showWarning", newVal);
      },
    },
    lastMenstrualPeriod: {
      handler(newVal) {
        let cleaned = newVal.replace(/\D/g, "");
        if (cleaned.length > 8) {
          cleaned = cleaned.slice(0, 8);
        }
        let formatted = "";
        if (cleaned.length > 0) {
          formatted = cleaned.slice(0, 2);
        }
        if (cleaned.length > 2) {
          formatted += "/" + cleaned.slice(2, 4);
        }
        if (cleaned.length > 4) {
          formatted += "/" + cleaned.slice(4, 8);
        }
        if (formatted !== newVal) {
          this.lastMenstrualPeriod = formatted;
        }
      },
    },
    boneDensityScan: {
      immediate: true,
      handler: function (newValue) {
        if (!newValue) {
          this.setHormoneZoomerQnrSingleField(
            "bone_health_and_toxic_exposure.bone_density_result",
            null
          );
        }
      },
    },
    undergoingHormoneTherapy: {
      immediate: true,
      handler: function (newValue) {
        if (!newValue) {
          this.setHormoneZoomerQnrSingleField(
            "treatment_considerations.current_medications",
            ""
          );
        }
      },
    },
    lastMenstrualDate: {
      immediate: true,
      handler(newVal) {
        this.lastMenstrualPeriod = newVal;
      },
    },
  },
  emits: ["removeWarning"],
  computed: {
    ...mapState(()=>useHormoneZoomerStore(), [
      "hormoneZoomerQnr",
      "personalHistoryArray",
      "comorbiditiesArray",
      "familyHistoryArray",
      "lastMenstrualDate",
      "qnrIsCompleted",
    ]),
    isMobile() {
      return this.width <= 808;
    },
    dynamic_input_type() {
      return this.isMobile ? "textarea" : "text";
    },
    boneDensityScan() {
      return this.hormoneZoomerQnr.bone_health_and_toxic_exposure
        .bone_density_scan;
    },
    undergoingHormoneTherapy() {
      return this.hormoneZoomerQnr.treatment_considerations
        .undergoing_hormone_therapy;
    },
  },
  methods: {
    ...mapActions(()=>useHormoneZoomerStore(), [
      "getZoomerQnrData",
      "submitHormoneZoomerQnr",
      "setHormoneZoomerQnrSingleField",
      "setPersonalHistoryArray",
      "setComorbiditiesArray",
      "setFamilyHistoryArray",
      "setLastMenstrualDate",
      "convertHormoneZoomerQnrArrayToObject",
    ]),
    handleValueChange(key, value) {
      this.setHormoneZoomerQnrSingleField(key, value);
    },
    handlePersonHistoryChange(value) {
      this.setPersonalHistoryArray(value);
      // convert array value to boolean
      this.convertHormoneZoomerQnrArrayToObject("personal_history", value);
    },
    handleComorbidityChange(value) {
      this.setComorbiditiesArray(value);
      // convert array value to boolean
      this.convertHormoneZoomerQnrArrayToObject("comorbidities", value);
    },
    handleFamilyHistoryChange(value) {
      this.setFamilyHistoryArray(value);
      // convert array value to boolean
      this.convertHormoneZoomerQnrArrayToObject("family_history", value);
    },
    handleMenstrualDateChange() {
      if (
        !moment(this.lastMenstrualPeriod).isValid() ||
        this.lastMenstrualPeriod?.length !== 10
      ) {
        console.log("Invalid date");
        this.lastMenstrualPeriod = "";
        return;
      }
      this.setLastMenstrualDate(this.lastMenstrualPeriod);
      let date = moment(this.lastMenstrualPeriod, "MM/DD/YYYY").format(
        "YYYY-MM-DD"
      );
      this.setHormoneZoomerQnrSingleField(
        "basic_info.last_menstrual_period",
        date
      );
    },
    removeWarning() {
      this.$emit("removeWarning");
    },
  },
};
</script>

<style scoped>
.subpartContainer {
  width: 100%;
  padding: 0px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.title {
  font-size: 16px;
  font-weight: 600;
}

.col-container {
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
}

.inputField {
  height: 42px;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.4px;
  color: #000000;
  border-radius: 4px;
  width: 593px;
}

.expandInputField {
  height: 42px;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.4px;
  color: #000000;
  border-radius: 4px;
  width: 760px;
}

:deep(.el-textarea__inner) {
  caret-color: #000 !important;
  white-space: normal;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.4px;
  color: #000000;
  resize: none;
  height: 126px;
  box-shadow: none;
  padding: 12px 16px;
  border: 1px solid #bcccdc;
}

:deep(.el-input__wrapper) {
  box-shadow: none;
  border: 1px solid #bcccdc;
  padding-left: 21px;
}

.subTitleContainer {
  padding: 12px 24px;
  border-radius: 4px;
  background-color: #faece6;
  margin-top: 36px;
  margin-bottom: 24px;
}

.subTitle {
  color: #0e4672;
  font-family: Roboto;
  font-weight: 500;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.17px;
}

.assessmentHint {
  font-family: Roboto;
  font-size: 14px;
  margin-top: 8px;
}

.assessmentHint b {
  font-weight: 700;
  font-style: italic;
}

.expandedContainer {
  padding: 12px 24px;
  border-radius: 4px;
  background-color: #f3f6f8;
  margin: 12px 12px;
}

:deep(.el-input__inner) {
  color: #000000;
}

:deep(.el-input.is-disabled .el-input__wrapper) {
  background-color: #f2f2f2;
}

:deep(.el-input.is-disabled .el-input__inner) {
  -webkit-text-fill-color: #1c1c1c;
}

:deep(.el-textarea.is-disabled .el-textarea__inner) {
  background-color: #f2f2f2;
  -webkit-text-fill-color: #1c1c1c;
}

@media only screen and (max-width: 808px) {
  .subpartContainer {
    width: 100%;
    padding: 0px 8px;
    margin-bottom: 12px;
    flex-direction: column;
    align-items: flex-start;
  }

  .inputField {
    width: 334px;
    height: 42px;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.4px;
  }

  .subpartContainer .title {
    font-size: 14px;
  }

  .subTitleContainer {
    padding: 12px 16px;
    margin-top: 24px;
    margin-bottom: 12px;
  }

  .subTitle {
    font-size: 14px;
    line-height: 16px;
  }

  .expandedContainer {
    padding: 12px 16px;
    margin: 12px 8px;
    font-size: 14px;
  }

  .assessmentHint {
    font-size: 14px;
    line-height: 14px;
  }

  .assessmentHint b {
    display: block;
  }

  :deep(.el-textarea__inner) {
    font-size: 14px;
    line-height: 16px;
  }

  .expandInputField{
    height: 100px;
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0.4px;
    color: #000000;
    border-radius: 4px;
    width: 310px;
  }

  .expandedContainer :deep(.el-textarea__inner) {
    height: 100px;
  }
}
</style>
